import qs from "qs"
import { Fragment, useEffect, useState, useCallback, useMemo } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, ToggleLeftIcon, ToggleRightIcon, Trash2Icon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import { Outlet, useLoaderData, useFetcher, useRouteLoaderData, useSearchParams, useOutletContext } from "react-router"
import { useForm, Controller, type FieldErrors, type FieldValues } from "react-hook-form"
import z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { fetchAPI, handleResponse, reactHookFormSubmitShim, throwIfNotOk } from "@/utils"
import { FiltersWrapper, type StoredSession, type OnBeforeSubmit } from "@/components/Filters"
import type { RootLoader } from "../root"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { hasPermission } from "@/machines/Authmachine"
import Pagination from "@/components/ui/pagination"
import {
  formatFluentValidationErrors,
  formatRHFErrors,
  type FluentValidationError,
  type ValidationSummaryData,
} from "@/utils/forms"
import { ValidationSummary } from "@/components/ui/ValidationSummary"
import {
  StateSchema,
  StateToggleActiveSchema,
  StateEditOrCreateSchema,
  StateDeleteSchema,
  FLAG_FIELDS,
  type State,
} from "@/schemas/reviewer/states"

const routeId = "states-routeId"
const sessionKey = "states-filter"

const SEARCH_TYPE_MAP: Record<string, string> = {
  "Starts With": "StartsWith",
  "Ends With": "EndsWith",
  Contains: "Contains",
  Exact: "Exact",
}

function isResponse(error: unknown): error is Response {
  return error instanceof Response
}

function coerceBooleans(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === "true") result[k] = true
    else if (v === "false") result[k] = false
    else result[k] = v
  }
  return result
}

interface ActionReturn {
  action: "CREATE" | "UPDATE" | "DELETE" | "TOGGLEACTIVE" | "UNKNOWN"
  errorMessage?: string
  errors: Record<string, string[]>
}

interface StatesContext {
  onSuccess: (message: string) => void
  onError: (errors: Record<string, string[]>) => void
  globalErrors: Record<string, string[]>
  message: string | null
}

type DeleteValues = z.infer<typeof StateDeleteSchema>
type EditOrCreateValues = z.infer<typeof StateEditOrCreateSchema>

function normaliseStates(raw: unknown, organisation: string): State[] {
  if (!Array.isArray(raw)) return []
  return raw.map((item: unknown) => {
    if (typeof item === "string") {
      return {
        organisation,
        project: null,
        name: item,
        showReadonly: false,
        showEscalate: false,
        showInTasks: false,
        showComplete: false,
        isQA: false,
        allowReopen: false,
        hiddenProcess: false,
        active: true,
      } satisfies State
    }
    return { ...(item as State), organisation } satisfies State
  })
}

async function loader({ request }: { request: Request }) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const url = new URL(request.url)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]
  const includeInactive = (filters.raw as Record<string, string> | undefined)?.includeInactive === "true"
  const searchName = (filters.raw as Record<string, string> | undefined)?.name ?? ""
  const searchType = (filters.raw as Record<string, string> | undefined)?.nameCriteria ?? "Starts With"
  const mappedSearchType = SEARCH_TYPE_MAP[searchType] ?? "Contains"

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedProject = (filters.raw as Record<string, string> | undefined)?.project ?? undefined

  const projectQuery = loadedProject != null ? `project=${encodeURIComponent(loadedProject)}` : ""
  const searchTypeQuery =
    searchName !== "" ? `name=${encodeURIComponent(searchName)}&searchType=${encodeURIComponent(mappedSearchType)}` : ""
  const includeInactiveQuery = includeInactive ? `includeInactive=true` : ""
  const queryParts = [projectQuery, searchTypeQuery, includeInactiveQuery].filter(Boolean).join("&")
  const finalUrl = `/api/unityReview/States/${loadedOrganisation}${queryParts !== "" ? `?${queryParts}` : ""}`

  try {
    const raw = await fetchAPI<unknown>(finalUrl).then(handleResponse)

    const allStates = normaliseStates(raw, loadedOrganisation)

    const filtered =
      searchName !== "" ? allStates.filter((s) => s.name.toLowerCase().includes(searchName.toLowerCase())) : allStates

    const totalCount = filtered.length
    const paginated = filtered.slice((page - 1) * pageSize, page * pageSize)

    return { states: paginated, loadedOrganisation, loadedProject, projects, totalCount, page }
  } catch (error) {
    if (error != null && typeof error === "object" && "status" in error && error.status === 404) {
      return { states: [], loadedOrganisation, loadedProject, projects, totalCount: 0, page: 1 }
    }
    throw error
  }
}

async function mainAction({ request }: { request: Request }): Promise<ActionReturn> {
  const rawBody = coerceBooleans(qs.parse(await request.text()))
  const { data, success, error } = StateSchema.safeParse(rawBody)

  if (!success) {
    console.log(error)
    return { action: "UNKNOWN", errorMessage: "Failed to parse the form contents", errors: {} }
  }

  let errorMessage: string | undefined
  let errors: Record<string, string[]> = {}

  const projectQuery = data.project !== "" ? `?project=${encodeURIComponent(data.project)}` : ""

  switch (data.action) {
    case "CREATE":
    case "UPDATE": {
      const { action, organisation, name, project: _p, ...payload } = data
      try {
        await fetchAPI(`/api/unityReview/States/${organisation}/${name}${projectQuery}`, {
          method: "POST",
          body: JSON.stringify({
            ...payload,
            ...(action === "CREATE" && { active: true }),
          }),
        }).then(throwIfNotOk)
      } catch (err) {
        if (isResponse(err) && err.status === 400) {
          errors = formatFluentValidationErrors((await err.json()) as FluentValidationError[])
        } else {
          console.error(err)
          errorMessage = action === "CREATE" ? "Error creating state" : "Error updating state"
        }
      }
      break
    }
    case "DELETE": {
      try {
        await fetchAPI(`/api/unityReview/States/${data.organisation}/${data.name}${projectQuery}`, {
          method: "DELETE",
        }).then(throwIfNotOk)
      } catch (err) {
        if (isResponse(err) && err.status === 409) {
          errorMessage = "This state is currently in use and cannot be deleted."
        } else if (isResponse(err) && err.status === 400) {
          errors = formatFluentValidationErrors((await err.json()) as FluentValidationError[])
        } else {
          console.error(err)
          errorMessage = "Error deleting state"
        }
      }
      break
    }
    case "TOGGLEACTIVE": {
      try {
        await fetchAPI(`/api/unityReview/States/${data.organisation}/${data.name}${projectQuery}`, {
          method: "PUT",
          body: JSON.stringify({ active: !data.active }),
        }).then(throwIfNotOk)
      } catch (err) {
        if (isResponse(err) && err.status === 400) {
          errors = formatFluentValidationErrors((await err.json()) as FluentValidationError[])
        } else {
          console.error(err)
          errorMessage = "Error toggling state active status"
        }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action: data.action, errorMessage, errors }
}

function ToggleActive({
  state,
  onError,
  onSuccess,
}: {
  state: State
  onError: (errors: ValidationSummaryData) => void
  onSuccess: () => void
}) {
  const fetcher = useFetcher<ActionReturn>({ key: "states_silent_forms" })
  const isSubmitting = fetcher.state !== "idle"

  const handleClick = () => {
    const parsed = StateToggleActiveSchema.safeParse({
      action: "TOGGLEACTIVE",
      organisation: state.organisation,
      project: state.project ?? "",
      name: state.name,
      active: state.active,
    })

    if (!parsed.success) {
      onError({ "": ["Invalid state data, cannot toggle active status"] })
      return
    }

    fetcher
      .submit(
        qs.stringify({
          action: "TOGGLEACTIVE",
          organisation: state.organisation,
          project: state.project ?? "",
          name: state.name,
          active: String(state.active),
        }),
        { method: "POST" },
      )
      .then(() => onSuccess())
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          disabled={isSubmitting}
          size="icon"
          variant="ghost"
          type="button"
          onClick={handleClick}
          className="h-8 w-8 cursor-pointer opacity-50"
        >
          {state.active ? (
            <ToggleRightIcon className="h-4 w-4 text-green-600" />
          ) : (
            <ToggleLeftIcon className="h-4 w-4 text-gray-600" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{state.active ? "Deactivate" : "Activate"}</TooltipContent>
    </Tooltip>
  )
}

function StateDeleteForm({
  state,
  onError,
  onSuccess,
}: {
  state: State
  onError: (errors: ValidationSummaryData) => void
  onSuccess: () => void
}) {
  const { register, handleSubmit } = useForm<DeleteValues>({
    resolver: zodResolver(StateDeleteSchema),
    values: {
      action: "DELETE" as const,
      organisation: state.organisation,
      project: state.project ?? "",
      name: state.name,
    },
  })

  const fetcher = useFetcher<ActionReturn>()

  const onSubmit = handleSubmit(
    (formData: DeleteValues) => {
      reactHookFormSubmitShim(formData, fetcher.submit)
    },
    (validationErrors: FieldErrors<FieldValues>) => {
      onError(formatRHFErrors(validationErrors))
    },
  )

  useEffect(() => {
    const result = fetcher.data
    if (result?.action !== "DELETE") return

    const hasNoErrors =
      (result.errors == null || Object.keys(result.errors).length === 0) && result.errorMessage == null
    if (hasNoErrors) {
      onSuccess()
    } else if (result.errorMessage != null) {
      onError({ "": [result.errorMessage] })
    }
  }, [fetcher.data, onError, onSuccess])

  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete State</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" className="max-w-sm">
        <p className="text-sm text-pretty break-all">
          State &quot;<span className="font-semibold">{state.name}</span>&quot; will be permanently deleted. Are you
          sure?
        </p>
        <fetcher.Form
          method="POST"
          className="flex items-center justify-start gap-3 pt-4"
          onSubmit={(e) => void onSubmit(e)}
        >
          <input type="hidden" {...register("action")} />
          <input type="hidden" {...register("organisation")} />
          <input type="hidden" {...register("project")} />
          <input type="hidden" {...register("name")} />
          {fetcher.state !== "idle" && fetcher.formData?.get("name") === state.name && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button variant="ghost">Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="destructive">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

function StateEditOrCreateForm({ state, closeDialog }: { state?: State; closeDialog: () => void }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const rootData = useRouteLoaderData<RootLoader>("root")
  const fetcher = useFetcher<typeof mainAction>()

  const isEditing = state != null

  const getCurrentOrganisation = useCallback(() => {
    if (data?.loadedOrganisation != null && data.loadedOrganisation !== "") return data.loadedOrganisation
    const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
    return filters.parsed?.organisation ?? rootData?.organisations[0] ?? ""
  }, [data?.loadedOrganisation, rootData?.organisations])

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    control,
    formState: { errors, isValid },
  } = useForm<EditOrCreateValues>({
    resolver: zodResolver(StateEditOrCreateSchema),
    mode: "onChange",
    values: {
      action: isEditing ? "UPDATE" : "CREATE",
      organisation: state?.organisation ?? getCurrentOrganisation(),
      project: state?.project ?? data?.loadedProject ?? "",
      name: state?.name ?? "",
      showReadonly: state?.showReadonly ?? false,
      showEscalate: state?.showEscalate ?? false,
      showInTasks: state?.showInTasks ?? false,
      showComplete: state?.showComplete ?? false,
      isQA: state?.isQA ?? false,
      allowReopen: state?.allowReopen ?? false,
      hiddenProcess: state?.hiddenProcess ?? false,
    },
  })

  const zodSummary = formatRHFErrors(errors)
  const serverSummary = fetcher.data?.errors
  const combinedErrors = { ...zodSummary, ...serverSummary }

  useEffect(() => {
    const result = fetcher.data
    if (result == null) return
    const hasNoValidationErrors = result.errors == null || Object.keys(result.errors).length === 0
    const hasNoGlobalError = result.errorMessage == null
    if (hasNoGlobalError && hasNoValidationErrors) {
      closeDialog()
      if (!isEditing) reset()
    }
  }, [closeDialog, isEditing, reset, fetcher.data])

  if (data == null || !hasPermission("writeAccess")) return null

  const projects = data.projects ?? []

  const onSubmit = handleSubmit(
    async (formData: EditOrCreateValues) => {
      if (!isEditing) {
        const projectQuery = formData.project !== "" ? `?project=${encodeURIComponent(formData.project)}` : ""
        try {
          const response = await fetchAPI<State>(
            `/api/unityReview/States/${data.loadedOrganisation}/${formData.name}${projectQuery}`,
          ).then(handleResponse)

          if (response != null && Object.keys(response).length > 0) {
            setError("name", {
              message: "That state name already exists within this organisation, please choose a unique name",
            })
            return
          }
        } catch (err) {
          if (!isResponse(err) || (err.status !== 404 && err.status !== 204)) {
            console.error(err)
            setError("name", { message: "There was a problem identifying if this is a duplicate entry or not" })
            return
          }
        }
      }

      clearErrors()
      fetcher.submit(formData, { method: "POST" })
    },
    (validationErrors: FieldErrors<FieldValues>) => {
      formatRHFErrors(validationErrors)
    },
  )

  return (
    <fetcher.Form method="POST" className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />

      <ValidationSummary errors={combinedErrors} />

      <div>
        <label className="mb-2 block text-sm font-medium">
          Project <span className="text-xs text-gray-400">(optional)</span>
        </label>
        {isEditing ? (
          <>
            <Input value={state.project ?? ""} disabled className="bg-gray-50" />
            <p className="mt-1 text-xs text-gray-500">Project cannot be changed after creation</p>
            <input type="hidden" {...register("project")} />
          </>
        ) : projects.length > 0 ? (
          <Controller
            name="project"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(val) => {
                  field.onChange(val === "all" ? "" : val)
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All projects</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        ) : (
          <Input {...register("project")} placeholder="Enter project (leave blank for all projects)" />
        )}
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">Name</label>
        {isEditing ? (
          <>
            <Input {...register("name")} disabled className="bg-gray-50" />
            <p className="mt-1 text-xs text-gray-500">Name cannot be changed after creation</p>
          </>
        ) : (
          <Input {...register("name")} aria-required="true" placeholder="Enter unique state name" />
        )}
      </div>

      <div>
        <p className="mb-3 text-sm font-medium">Configuration Flags</p>
        <div className="grid grid-cols-2 gap-3 rounded-md border p-4">
          {FLAG_FIELDS.map(({ key, label }: { key: keyof EditOrCreateValues; label: string }) => (
            <Controller
              key={key}
              name={key}
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <input type="hidden" name={key} value={String(field.value)} />
                  <Checkbox
                    id={key}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                  />
                  <label htmlFor={key} className="cursor-pointer text-sm font-medium select-none">
                    {label}
                  </label>
                </div>
              )}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> {isEditing ? "Updating" : "Creating"}
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit" disabled={!isValid || fetcher.state !== "idle"}>
          {isEditing ? "Update" : "Create"}
        </Button>
      </div>
    </fetcher.Form>
  )
}

function StateEditOrCreate({ state, onSuccess }: { state?: State; onSuccess?: () => void }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [open, setOpen] = useState(false)

  const isEditing = state != null

  const closeDialog = useCallback(() => {
    setOpen(false)
    onSuccess?.()
  }, [onSuccess])

  if (data == null || hasPermission("writeAccess") !== true) return null

  const TriggerButton = isEditing ? (
    <Button variant="ghost" size="icon" className="h-8 w-8">
      <PencilIcon className="h-4 w-4" />
      <span className="sr-only">Edit State</span>
    </Button>
  ) : (
    <Button variant="outline">
      <PlusIcon className="mr-2 h-4 w-4" /> Add New
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isEditing ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Edit State</TooltipContent>
        </Tooltip>
      ) : (
        <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      )}

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add New"} State</DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <>
                Editing <span className="font-semibold">{state.name}</span>
              </>
            ) : (
              "Create a new workflow state"
            )}
          </DialogDescription>
        </DialogHeader>
        <StateEditOrCreateForm state={state} closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()
  const url = new URL(window.location.href)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)
  const { onSuccess, onError, globalErrors, message } = useOutletContext<StatesContext>()

  const pagingAdjustment = (page - 1) * pageSize
  const fetcher = useFetcher<ActionReturn>({ key: "states_silent_forms" })

  const serverErrors = fetcher.data?.errors
  const combinedErrors = { ...globalErrors, ...serverErrors }

  const handleUpdateSuccess = useCallback(() => onSuccess("State updated successfully."), [onSuccess])

  const handleToggleSuccess = useCallback(() => onSuccess("State toggled successfully."), [onSuccess])

  const handleDeleteSuccess = useCallback(() => onSuccess("State deleted successfully."), [onSuccess])

  return (
    <div className="h-screen overflow-y-auto">
      <Pagination routeId={routeId} />
      {message != null && (
        <p className="mb-2 rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
          {message}
        </p>
      )}
      <ValidationSummary errors={combinedErrors} />
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            <TableHead className="text-left">Name</TableHead>
            <TableHead className="w-36 text-left">Project</TableHead>
            <TableHead className="w-28 text-left">Active</TableHead>
            <TableHead className="w-28 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.states.length > 0 ? (
            data.states.map((item: State, index: number) => (
              <TableRow key={`${item.organisation}-${item.project ?? ""}-${item.name}`} className="text-sm">
                <TableCell>{index + 1 + pagingAdjustment}.</TableCell>
                <TableCell className="font-medium break-words whitespace-normal">{item.name}</TableCell>
                <TableCell className="text-gray-500">
                  {item.project ?? <span className="text-gray-400 italic">—</span>}
                </TableCell>
                <TableCell>
                  <Badge variant={item.active ? "default" : "secondary"}>{item.active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <StateEditOrCreate state={item} onSuccess={handleUpdateSuccess} />
                    <ToggleActive state={item} onError={onError} onSuccess={handleToggleSuccess} />
                    <StateDeleteForm state={item} onError={onError} onSuccess={handleDeleteSuccess} />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                No states found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

export function States() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [, setSearchParams] = useSearchParams()

  const [globalErrors, setGlobalErrors] = useState<Record<string, string[]>>({})
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleSuccess = useCallback((message: string) => {
    setGlobalErrors({})
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(null), 3000)
  }, [])

  const context: StatesContext = useMemo(
    () => ({
      onSuccess: handleSuccess,
      onError: setGlobalErrors,
      globalErrors,
      message: successMessage,
    }),
    [handleSuccess, globalErrors, successMessage],
  )

  const onAddSuccess = useCallback(() => handleSuccess("State created successfully."), [handleSuccess])

  const onBeforeSubmit = useCallback<OnBeforeSubmit>(
    (formData, reason) => {
      if (reason.type !== "field") return
      if (reason.name === "organisation") {
        formData.delete("project")
        setSearchParams((urlSearchParams) => {
          urlSearchParams.delete("page")
          return urlSearchParams
        })
      }
      if (reason.name === "includeInactive") {
        setSearchParams((urlSearchParams) => {
          urlSearchParams.delete("page")
          return urlSearchParams
        })
      }
    },
    [setSearchParams],
  )

  return (
    <Fragment>
      <div className="flex items-center gap-4">
        {rootData?.organisations != null && rootData.organisations.length > 0 && (
          <FiltersWrapper submitOnChange={true} canReset={true} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data?.loadedOrganisation ?? rootData.organisations[0]}
              items={rootData.organisations}
            />
            {data != null && data.projects.length > 0 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                placeholder="All projects"
                items={data.projects}
              />
            )}
            <FiltersWrapper.Input name="name" label="Name" placeholder="Search by name…" />
            <FiltersWrapper.Checkbox name="includeInactive" label="Include Inactive" />
          </FiltersWrapper>
        )}
        <div className="flex-1" />
        <StateEditOrCreate onSuccess={onAddSuccess} />
      </div>
      <Outlet context={context} />
    </Fragment>
  )
}

States.Main = Main
States.loader = loader
States.action = mainAction
States.routeId = routeId

Main.action = mainAction
